import React, { Fragment, useEffect, useState, useMemo, useCallback } from 'react';
import { Card, CardBody, Container } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import EarnHistoryTable from './EarnHistoryTable';
import { getTotalEarnHistoryAPI } from '../../../../Library/apis';
import { Breadcrumbs } from '../../../../AbstractElements';
import SubHeader from '../../Common/SubHeader';
import FilterHeader from '../../Common/FilterHeader';

const TERM_OPTIONS = [
  { value: '',    label: 'All Terms' },
  { value: '1',   label: '2 Hours'  },
  { value: '30',  label: '30 Days'  },
  { value: '60',  label: '60 Days'  },
  { value: '90',  label: '90 Days'  },
  { value: '180', label: '180 Days' },
  { value: '365', label: '365 Days' },
];

const fields = [
  { key: 'firstName', label: 'First Name', type: 'text', placeholder: 'e.g. John' },
  { key: 'lastName', label: 'Last Name', type: 'text', placeholder: 'e.g. Doe' },
  { key: 'userId', label: 'User ID', type: 'text', placeholder: 'e.g. 4000000000' },
  { key: 'term', label: 'Term', type: 'select', options: TERM_OPTIONS },
  { key: 'createdAt', label: 'Date Range', type: 'daterange', colClass: 'col-12 col-md-4 col-lg-2' },
];

const EarnInterest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const LIMIT = 20;

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  
  const [currentPage, setCurrentPage] = useState(Number(queryParams.get('page')) || 1);
  const [lastKnownPage, setLastKnownPage] = useState(Number(queryParams.get('page')) || 1);
  
  const [activeFilters, setActiveFilters] = useState({
    firstName: queryParams.get('firstName') || '',
    lastName: queryParams.get('lastName') || '',
    userId: queryParams.get('userId') || '',
    term: queryParams.get('term') || '',
    dateFrom: queryParams.get('dateFrom') || '',
    dateTo: queryParams.get('dateTo') || '',
  });

  const fetchData = useCallback((newFilters, newPage) => {
    // Sync URL parameters
    const params = new URLSearchParams();
    if (newPage > 1) params.set('page', newPage);
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key]) params.set(key, newFilters[key]);
    });
    navigate({ search: params.toString() }, { replace: true });

    setIsLoading(true);
    getTotalEarnHistoryAPI(newFilters, newPage, LIMIT)
      .then(res => {
        setData(res.body || []);
        setTotalRows(res.pagination?.total || 0);
        setCurrentPage(newPage);
        setActiveFilters(newFilters);

        // Memory Logic: If no active filters, remember this page as the browsing baseline
        const isSearching = Object.values(newFilters).some(v => !!v);
        if (!isSearching) {
          setLastKnownPage(newPage);
        }
      })
      .catch((err) => {
        console.error("Fetch error:", err);
        setData([]);
        setTotalRows(0);
      })
      .finally(() => setIsLoading(false));
  }, [navigate]);

  useEffect(() => {
    fetchData(activeFilters, currentPage);
  }, []);

  const handleSearch = (currentFilters) => {
    const payload = {
      firstName: currentFilters.firstName,
      lastName:  currentFilters.lastName,
      userId:    currentFilters.userId,
      term:      currentFilters.term,
      dateFrom:  currentFilters.createdAtFrom,
      dateTo:    currentFilters.createdAtTo,
    };
    fetchData(payload, 1);
  };

  const handlePageChange = (page) => fetchData(activeFilters, page);

  const handleReset = () => fetchData({}, lastKnownPage);
  
  const mappedInitialFilters = useMemo(() => ({
  ...activeFilters,
  createdAtFrom: activeFilters?.dateFrom,
  createdAtTo: activeFilters?.dateTo,
}), [activeFilters]);
  const handleRefresh = () => fetchData(activeFilters, currentPage);
  const hasActiveFilters = Object.values(activeFilters).some(val => !!val);
  const shouldShowSearch = totalRows > 0 || hasActiveFilters;
   
  return (
    <Fragment>
      <Breadcrumbs mainTitle='Reports/Records' />
      <Container fluid={true}>
        <SubHeader title="Interests" data={data} />

        {shouldShowSearch && (
          <FilterHeader
            title="Filter Records"
            fields={fields}
            onSearch={handleSearch}
            onRefresh={handleRefresh}
            onReset={handleReset}
            isLoading={isLoading}  
            initialFilters={mappedInitialFilters}
          />
        )}

        <Card className="mt-3">
          <CardBody>
            <EarnHistoryTable
              data={data}
              isLoading={isLoading}
              totalRows={totalRows}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              limit={LIMIT}
            />
          </CardBody>
        </Card>
      </Container>
    </Fragment>
  );
};

export default EarnInterest;